import { createMemo, createRoot, createSignal, type Accessor } from 'solid-js';

import {
  EVENT_ENTITY_DAMAGED,
  EVENT_ENTITY_DIED,
  EVENT_LEVEL_UP,
  FRAME_CHAT,
  FRAME_PARTY,
  FRAME_SOCIAL,
  EVENT_PLAYER_DIED,
  EVENT_AREA_STRIKE,
  EVENT_SKILL_CAST,
  EVENT_SKILL_CAST_STARTED,
  EVENT_SKILL_REFUSED,
  EVENT_SKILL_LEVEL_REFUSED,
  EVENT_POTION_REFUSED,
  EVENT_DODGED,
  EVENT_DODGE_REFUSED,
  EVENT_SELL_REFUSED,
  EVENT_BUY_REFUSED,
  EVENT_GATE_REFUSED,
  EVENT_STATION_REFUSED,
  EVENT_ITEM_MOVE_REFUSED,
  EVENT_TEST_REFUSED,
  FRAME_SNAPSHOT,
  FRAME_WELCOME,
  FRAME_CHARACTER,
  FRAME_INVENTORY,
  FRAME_ZONE,
  pickup,
  type ChatMessage,
  type Command,
  type PartyPayload,
  type SocialPayload,
  type DamageType,
  type GameEvent,
  type GroundItem,
  type RefusalReason,
  type CharacterPayload,
  type SkillInfo,
  type SkillState,
  type PassiveInfo,
  type PassiveState,
  type StationKind,
  type TestModeInfo,
  type WelcomePayload,
  type ContainerId,
  type ContainerPayload,
  type VaultPayload,
  type VendorPayload,
  type DollSlot,
  type EquippedItem,
  type EntitySnapshot,
  type ServerFrame,
  type TownPortal,
  type ZonePayload,
  type AoeShape,
} from '../net/protocol';
import { SnapshotAssembler } from '../net/rehydrate';
import { WireLog } from '../net/wirelog';
import { renderFeatures } from '../game/compat';
import { damageTypeOf } from '../game/damage';
// ── Loot filter (Phase 12) ───────────────────────────────────────────────────
import {
  compileFilter,
  DEFAULT_FILTER_TEXT,
  evaluateItem,
  groundFilterItem,
  loadFilterText,
  planVacuum,
  saveFilterText,
  vacuumCandidateCount,
  type CompiledFilter,
  type FilterContext,
  type FilterDiagnostic,
  type FilterVerdict,
  type VacuumPlan,
} from '../game/filter';
// ── end loot filter imports ──────────────────────────────────────────────────
import {
  DORMANT_STAMINA,
  type ApocalypseState,
  type RebirthSummary,
  type StaminaState,
} from './session';
import {
  browserStorage,
  DEFAULT_SETTINGS,
  loadSettings,
  saveSettings,
  type Settings,
  type SettingsStore,
  type SliderKey,
  type ToggleKey,
} from './settings';

/** How long a floating damage number stays on screen, in milliseconds. */
export const DAMAGE_TTL_MS = 900;

/**
 * How long the screen-edge wash lasts after the local player is hit.
 *
 * Much shorter than `DAMAGE_TTL_MS`, and deliberately: the number is something to
 * read and the flash is something to feel. A quarter of a second is long enough to
 * register in peripheral vision and short enough that a monster swinging every 1.2
 * seconds does not leave the screen permanently red — which is what a wash sharing
 * the number's lifetime would do.
 */
export const HURT_FLASH_MS = 260;

/** How long the action bar shows why a cast was refused, in milliseconds. */
export const NOTICE_TTL_MS = 1800;

/**
 * A cast the render loop has not animated yet.
 *
 * Two queues rather than one, because a cast is no longer one instant: cast times give
 * it a beginning the animation plays from and an end where the outcome lands, and for
 * an ultimate those are 25 ticks apart. `takeCastStarts()` drains the beginnings and
 * `takeCasts()` drains the resolutions — one starts a body moving, the other throws a
 * projectile over damage that has already been applied.
 */
/**
 * A blast the render loop has not drawn yet.
 *
 * Queued rather than pushed through the reactive graph, exactly as a cast is: a ring is a
 * render-loop concern and no component displays it, so putting it in a signal would
 * schedule DOM work for something nothing renders.
 */
/** The local player's cast in progress. `startedAt` is on `castClock`'s scale (ms). */
export interface CastInProgress {
  skillId: string;
  name: string;
  /** The skill's cast window, `castTimeSec` from the catalogue. */
  seconds: number;
  startedAt: number;
}

/** The clock the cast bar is measured on — `performance.now()`, the one the HUD animates on. */
export const castClock = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

export interface PendingBlast {
  /** Whose it was — a caster or a monster. */
  entityId: string;
  /** The skill behind it, for the colour. Null for a monster's slam. */
  skillId: string | null;
  at: { x: number; z: number };
  radius: number;
  /** The geometry to draw. Absent from a monster's slam, which is always a circle. */
  shape?: AoeShape;
  /** A cone's full width in degrees. */
  angle?: number;
  /** The caster's bearing, for a cone or a line. */
  facing?: number;
}

export interface PendingCast {
  entityId: string;
  skillId: string;
  /** Where it went, for the projectile. Null when the cast had no direction. */
  at: { x: number; z: number } | null;
  /**
   * Where a dash is taking the caster and how long it has, when the skill displaces.
   *
   * Null for every other skill. Carried on the cast start rather than fetched from the catalogue
   * because the duration is per-character — see `dashSec` on the wire.
   */
  dash: { to: { x: number; z: number }; seconds: number } | null;
}

/**
 * A roll the render loop has not animated yet.
 *
 * Drained exactly like a cast, and for the same reason: an animation is a render-loop
 * concern, and pushing it through the reactive graph would schedule DOM work for
 * something no component displays.
 *
 * The **direction** is the payload. The snapshot's `DODGING` state says a roll is
 * happening but not which way, and a remote entity's interpolated position arrives two
 * ticks behind — so a body facing along its roll needs the vector from the event.
 */
export interface PendingDodge {
  entityId: string;
  direction: { x: number; z: number };
}

/**
 * Where a cast points, from either half of it.
 *
 * `aimX`/`aimZ` are `omitempty` on the wire, so an unaimed cast — a self-buff, a nova
 * with nothing under the cursor — arrives with neither key. Null rather than the
 * origin, because the origin is a perfectly good place to stand and is the middle of
 * every zone; `BoltField` reads null as "bloom on the caster".
 */
function castAim(ev: GameEvent): { x: number; z: number } | null {
  if (ev.aimX === undefined && ev.aimZ === undefined) return null;
  return { x: ev.aimX ?? 0, z: ev.aimZ ?? 0 };
}

/**
 * Everything the HUD and the predictor need to know about whether a roll can happen.
 *
 * The **client has to be able to answer this itself**, and that is the whole point of the
 * type existing. Predicting a roll the server refuses is a 4-unit error against a
 * 3.5-unit snap threshold, so an unconditional prediction is a visible rubber-band on
 * every press during the cooldown — which is a mashed key most of a fight. Both halves
 * now check the same two conditions.
 */
export interface DodgeState {
  /** True when a press will actually roll. The predictor gates on exactly this. */
  ready: boolean;
  /**
   * Seconds of cooldown left, **decremented locally between snapshots**.
   *
   * The server's value is 33 ms stale by the time the next frame renders, and refusing a
   * roll that became legal 20 ms ago is the inverse of the bug this fixes — the server
   * would grant it and the client would snap the other way.
   */
  cooldownRemaining: number;
  /** 0 → 1 of the cooldown still to run, for a radial sweep. 0 when ready. */
  cooldownFraction: number;
  /** True when stamina is the thing blocking it — a different message from a cooldown. */
  staminaShort: boolean;
  /** What a roll costs, from the CHARACTER frame. */
  cost: number;
  /**
   * False before the first CHARACTER frame.
   *
   * Nothing is known then, so the widget renders dormant rather than showing a ready
   * pip — the same rule the stamina bar follows, for the same reason: a plausible
   * indicator the player cannot tell is invented is worse than an honest empty one.
   */
  live: boolean;
}

/**
 * Nothing known yet — before the first CHARACTER frame.
 *
 * `ready: false`, so a press before the frame lands does not predict. It still *sends*,
 * and the server will grant it: a missed prediction on the very first roll of a session
 * costs one snap, where guessing `ready: true` would cost one on every early press.
 */
const DORMANT_DODGE: DodgeState = {
  ready: false,
  cooldownRemaining: 0,
  cooldownFraction: 0,
  staminaShort: false,
  cost: 0,
  live: false,
};

/**
 * Why the last action did not happen, for the HUD to explain.
 *
 * One notice for both the action bar and the potion belt, because they occupy the
 * same corner of the screen and two independent messages there would overlap. The
 * `kind` says which vocabulary to read `reason` in — a `COOLDOWN` on a cast and a
 * `COOLDOWN` on the belt are different sentences.
 */
/**
 * Why a skill rank was refused, and which skill it was about.
 *
 * Separate from `SkillNotice` because it is shown somewhere else: a refused cast
 * belongs in the action bar's corner, a refused rank belongs on the panel row the
 * player clicked.
 */
export interface SkillRankNotice {
  skillId: string;
  reason: RefusalReason;
}

export interface SkillNotice {
  kind: 'skill' | 'potion' | 'sell' | 'dodge' | 'station' | 'container' | 'gate' | 'buy';
  reason: RefusalReason;
  /** The action-bar slot that was pressed, 0-based. Meaningless for the belt. */
  slot: number;
  /** Which container a `container` refusal was about. Absent for every other kind. */
  container?: ContainerId;
  bornAt: number;
}

/**
 * An empty container: no cells and nothing in it.
 *
 * The seed value for the bag, and it is deliberately 0 × 0 rather than the shipped
 * size. Before the first INVENTORY frame the client does not know how big the bag is,
 * and inventing today's numbers would be a guess that happens to be right — the same
 * class of lie the flat 100/100 stamina placeholder was. A grid with no cells renders
 * as "nothing here", which is true.
 */
export const EMPTY_CONTAINER: ContainerPayload = { cols: 0, rows: 0, items: [] };

/**
 * An empty vault: room for nothing, and nothing in it.
 *
 * A capacity of zero for the same reason the bag seeds at 0 × 0 — it means "the server
 * has not said yet", and the panel reads it as *waiting* rather than as a vault the
 * player has filled. `0 of 0 stored` is not a limit anybody hit.
 */
export const EMPTY_VAULT: VaultPayload = { capacity: 0, items: [] };

/**
 * Where the player is in the shell: the title screen, or in the world.
 *
 * The socket does not connect until `enterGame()` — the title screen is a real
 * gate, not a decoration over a live session.
 */
export type Phase = 'title' | 'playing';

/** Full-screen panels. Only one can be open, and any of them locks the world input. */
/**
 * The full-screen panels. Exactly one is open at a time — they all cover the
 * canvas, so stacking them would leave a click with no unambiguous target.
 *
 * `system` is the escape menu: settings and the way out.
 */
/**
 * `portal` is the town level-select. It is in this union rather than being a local
 * signal inside `GrindPortal` for two reasons: the world-anchored **The Grind**
 * waypoint has to be able to open the same dialog, and a full-screen overlay must
 * suspend world input — `.level-select` covers the viewport, and while its open state
 * was private the clicks that landed on its backdrop fell through to the canvas and
 * walked the player around behind it.
 *
 * It is deliberately **not** in `game/keyboard.ts`'s `PANELS`: no key opens it, so it
 * gets no button in the HUD bar. Escape still closes it, because Escape closes
 * whatever is open.
 */
export type MenuPanel =
  | 'stash'
  | 'character'
  | 'skills'
  | 'passives'
  /**
   * Who is online, and who to ask to play.
   *
   * A full panel rather than a corner of the chat box: it holds a search, a list and an
   * invite that is waiting on an answer, and burying that under a chat tab would let it
   * expire unnoticed. In `PANELS`, because it opens unconditionally — there is always an
   * answer to *who is online*, even when the answer is nobody.
   */
  | 'social'
  | 'portal'
  /**
   * The loot-filter editor.
   *
   * A full panel rather than a section of the system menu: it is a text editor with a
   * diagnostics list under it, and it needs the room. It *is* in `game/keyboard.ts`'s
   * `PANELS` — unlike `portal` and `station` it opens unconditionally, anywhere, dead or
   * alive, so it earns its key and its button in the HUD bar.
   */
  | 'filter'
  /**
   * A town workshop. Which one is in `station()` — a single extra signal rather than
   * five panel values, because they are one panel with one body and five headings.
   */
  | 'station'
  /**
   * The developer panel, from Phase 1 of the roadmap.
   *
   * In this union rather than in a local signal for the reason `portal` is: it covers
   * the viewport, and a full-screen overlay whose open state is private does not
   * suspend world input — clicks on its backdrop would fall through to the canvas and
   * walk the player around behind it.
   *
   * Like `station` and `portal` it is deliberately **not** in `game/keyboard.ts`'s
   * `PANELS`: every entry there must open unconditionally, and this one exists only
   * when the server says test mode is on. Its key is routed by hand and its affordance
   * is `TestModeBar`, which is visible only where it works.
   */
  | 'test'
  | 'system'
  | null;

/** A damage number floating above an entity. */
export interface DamageNumber {
  id: number;
  entityId: string;
  amount: number;
  /** A critical strike, so the overlay can draw it larger. The server decided it. */
  crit: boolean;
  /**
   * What the hit was made of, defaulted from the wire's absent-means-physical.
   *
   * Resolved here rather than in the overlay so a component never has to know the
   * wire convention, and so every consumer of a number agrees about it.
   */
  damageType: DamageType;
  /**
   * How much of the blow the Mana Shield paid out of mana, when any of it was. Phase 39.
   *
   * **`amount` is the health lost and always has been**, so a shielded hit does not draw a number
   * the player did not take. This is the other half: without it a big blow reads as a small one for
   * no visible reason, and the mana orb dropping on the same frame is legible but not attributable.
   * Zero on every unshielded hit, which is nearly all of them.
   */
  absorbed: number;
  bornAt: number;
}

/**
 * A shot the render loop has not drawn yet.
 *
 * Drained beside the casts and the removals, for the same reason they are: a
 * projectile is a render-loop concern, and pushing it through the reactive graph
 * would schedule DOM work for something no component displays.
 *
 * It is the client's whole answer to a ranged monster. The hit resolved server-side
 * the instant it was sent, so the bolt is decoration over a settled outcome — the
 * same standing it has for a skill, and it lands the same fixed `BOLT_SECONDS` after
 * the number for the same reason.
 */
export interface PendingShot {
  /** Who fired, so the render loop can start the bolt at the body's real position. */
  sourceId: string;
  /** Who was hit. */
  targetId: string;
  damageType: DamageType;
}

/** Where an entity currently is on screen, for HUD elements anchored to it. */
export interface ScreenAnchor {
  x: number;
  y: number;
  visible: boolean;
}

export interface StoreOptions {
  /** Injectable clock, in milliseconds. Defaults to performance.now. */
  now?: () => number;
  /**
   * Where settings persist. Defaults to localStorage; pass null for none, or a
   * stub in tests. Explicitly injectable so a spec never depends on browser
   * storage surviving between cases.
   */
  storage?: SettingsStore | null;
}

/**
 * The single surface shared between the Babylon scene and the Solid HUD.
 *
 * Neither half talks to the other directly: the socket writes frames in here,
 * Solid reads the signals reactively, and the render loop reads `raw` — a plain
 * Map, deliberately outside the reactive graph so touching it 60 times a second
 * cannot schedule a single DOM update.
 */
/**
 * How many chat lines are kept.
 *
 * Two hundred: far more than a box shows, and small enough that a session left open all day
 * cannot grow without bound. The oldest go first, which is what scrollback means.
 */
const CHAT_LOG_LIMIT = 200;

export function createGameStore(opts: StoreOptions = {}) {
  const now = opts.now ?? (() => performance.now());
  const storage = opts.storage === undefined ? browserStorage() : opts.storage;

  return createRoot(() => {
    const [entities, setEntities] = createSignal<EntitySnapshot[]>([]);

    /**
     * The last complete world, so a frame carrying only the difference can be turned back
     * into one.
     *
     * Cleared on a zone change and on a reconnect, which are exactly the two moments the
     * server calls `Instance.forgetDescriptions` — the two halves agree by construction
     * rather than by timing.
     */
    const world = new SnapshotAssembler();
    /**
     * `?wirelog=1`: a console trace of deaths against removals.
     *
     * Off by default and allocating nothing when off. It exists because the three ways a
     * monster can end up frozen and unkillable on screen are indistinguishable by looking —
     * see `net/wirelog.ts`.
     */
    const wire = new WireLog();
    const [selfId, setSelfId] = createSignal<string | null>(null);
    const [tickRate, setTickRate] = createSignal(30);
    const [worldSize, setWorldSize] = createSignal(50);
    const [welcomeSpeed, setPlayerSpeed] = createSignal(5);
    // How long a walk takes to reach full speed and to come back to rest, from WELCOME.
    // Like the roll's shape and unlike the walk *speed*, nothing scales it with the
    // character, so there is no CHARACTER override to prefer.
    const [walkRampTime, setWalkRampTime] = createSignal(0.15);
    // The roll's trajectory, from WELCOME. Unlike the walk speed there is no CHARACTER
    // override to prefer: nothing scales a roll's distance or duration yet, so these
    // are the whole truth rather than a fallback.
    const [dodgeDistance, setDodgeDistance] = createSignal(4);
    const [dodgeDuration, setDodgeDuration] = createSignal(0.3);
    /**
     * How far a swing reaches, and how close counts as standing at a workshop — both
     * from WELCOME, both read by things the client does *before* the server answers.
     *
     * The reach is what a chase stops at: the client routes the walk, so it is the only
     * thing that can know where to stop. The station range is what an arrival intent
     * measures against. Neither is enforced here; the server re-checks both.
     */
    /**
     * How far the basic attack reaches, in world units.
     *
     * **Seeded from `WELCOME` and then overwritten by every `CHARACTER` frame**, because
     * it stopped being a constant: `attack_range` is a weapon's own stat, so a bow
     * reaches 13 and a sword 2. The chase has to stop at the *current* reach — a client
     * still using the boot value would walk a bow user into melee before firing.
     *
     * The WELCOME value survives as the unarmed baseline, which is what a character with
     * an empty hand derives anyway.
     */
    const [attackRange, setAttackRange] = createSignal(2);
    const [stationRange, setStationRange] = createSignal(4);
    // The two gate ranges, for the same reason: a click on a portal or an exit walks
    // there, and only the client can know where the walk should stop.
    const [portalEntryRange, setPortalEntryRange] = createSignal(2.5);
    const [exitRange, setExitRange] = createSignal(3.2);
    const [pickupRange, setPickupRange] = createSignal(2.6);
    /**
     * When the newest snapshot landed, so the dodge cooldown can be counted down
     * locally between them. See `dodgeState`.
     */
    const [snapshotAt, setSnapshotAt] = createSignal(0);
    const [connected, setConnected] = createSignal(false);

    /**
     * Set when the server replaced this connection with a newer one for the same character.
     *
     * **Terminal, and distinct from `connected`.** A displaced socket does not reconnect, so
     * showing *Reconnecting* over it would be a promise the client has no intention of
     * keeping — and a dead socket with no message is indistinguishable from a frozen game.
     */
    const [displaced, setDisplaced] = createSignal(false);

    // ── Party, social and chat ──────────────────────────────────────────────
    //
    // Three private, change-driven frames, held exactly as `character` and `inventory` are:
    // the server decides, the store keeps the last thing it said.
    const [party, setParty] = createSignal<PartyPayload>({ members: [] });
    const [social, setSocial] = createSignal<SocialPayload>({ online: [] });

    /**
     * The chat log, newest last, **capped**.
     *
     * A log that only grows is a leak on a server nobody logs out of — and a chat box shows
     * a few dozen lines at most, so anything past the cap is memory nothing can read. The
     * oldest go first, which is what scrollback means.
     */
    const [chatLog, setChatLog] = createSignal<ChatMessage[]>([]);
    const [targetId, setTargetId] = createSignal<string | null>(null);
    const [damageNumbers, setDamageNumbers] = createSignal<DamageNumber[]>([]);

    /**
     * The mob the pointer is over, or null.
     *
     * **Published here because the HUD needs it and the pointer handler owns it.** Hover lived
     * as a local in `game/input.ts`, which is the right place to *decide* it — that file does
     * the picking — and the wrong place to keep it, because `Nameplates` is the thing that now
     * shows a name on hover and cannot reach into a closure.
     *
     * Deliberately not reactive-per-frame: it is written on `pointermove` only when the answer
     * changes, so a still pointer costs nothing.
     */
    const [hoveredEntityId, setHoveredEntityId] = createSignal<string | null>(null);

    /**
     * When each entity was last damaged, by `performance.now()`.
     *
     * A plain `Map` rather than a signal: it is read inside `plateVerdict` on a frame that is
     * already re-rendering for the clock, and making it reactive would re-render every plate on
     * every hit anywhere in the zone.
     *
     * **Not derived from `damageNumbers`**, which is the obvious shortcut and wrong by a factor
     * of three: `DAMAGE_TTL_MS` is 900 ms because that is how long a floating number should be
     * readable, and the bar's window is 3 s. Tying them together made the bar flick out between
     * hits of a slow weapon.
     *
     * Entries are dropped with the entity, in the same sweep that forgets its anchor — otherwise
     * a zone's worth of dead monsters accumulates for the session.
     */
    const damagedAt = new Map<string, number>();

    /**
     * Bumped once per frame in which anything was damaged, purely so a plate can depend on it.
     *
     * **The Map is not reactive and the ordering is against us.** `applySnapshot` runs *before*
     * `applyEvents` on a snapshot frame, so the entity list — which is what re-renders the plates
     * — changes while `damagedAt` still holds the previous frame's answer. The bar for a mob hit
     * this tick would therefore appear on the *next* frame that happened to re-render it.
     *
     * In production that is one frame and invisible, because `frameNow` ticks. It is still the
     * kind of accidental correctness that stops being true the moment something is memoised, and
     * it made the behaviour untestable without faking a clock. One counter fixes both.
     *
     * A counter rather than a reactive `Map`: the identity of *what* was hit changes on nearly
     * every hit, so a reactive map would re-render every plate anyway — this says the same thing
     * for one number.
     */
    const [damageEpoch, setDamageEpoch] = createSignal(0);

    /**
     * Whether the player is holding the reveal key, which shows drops the filter hides.
     *
     * A signal rather than a poll of the keyboard, because it drives a render: `LootLabels` and
     * `LootMeshes` both read it, and they have to agree within the frame or a beam appears
     * without its name.
     */
    const [lootRevealed, setLootRevealed] = createSignal(false);

    /** When each ground drop was first seen, for the drop impulse. Keyed by drop id. */
    const groundSeenAt = new Map<string, number>();

    /**
     * Whether the ground list has been seen at least once for this zone.
     *
     * **Without it, joining a floor flashes everything on it.** The impulse fires for a drop the
     * client has not seen before, and on the first snapshot after a join or a zone change *every*
     * drop is new — measured in a browser at **27 labels at once**, with an overflow counter
     * reading "+27 more on the floor" for a floor nobody had touched.
     *
     * An earlier comment here argued the opposite: that first-seen-by-this-client was "the right
     * behaviour rather than a compromise". It is right for a drop that *lands*; it is wrong for a
     * floor that was already there, and this flag is exactly that difference.
     *
     * Reset with the ground on a zone change, so walking into a zone that already has loot in it
     * is the same quiet arrival.
     */
    let groundPrimed = false;
    const [notice, setNotice] = createSignal<SkillNotice | null>(null);
    const [anchors, setAnchors] = createSignal<Record<string, ScreenAnchor>>({});

    /**
     * A clock that advances once per rendered frame.
     *
     * **Reactive, because a countdown has to be.** A skill's cooldown arrives on the
     * CHARACTER frame, which is change-driven — the server counts cooldowns down every tick
     * and marks nothing dirty while it does, because that frame is the whole stat block plus
     * every skill and passive, and sending it at 30 Hz per player would undo the entire
     * bandwidth budget.
     *
     * So the sweep was rendering a number from the moment of the cast and never moving again
     * until something else marked the frame dirty. Reported exactly that way: *the overlay
     * only moves when we cast a spell, and does not follow cooldown time.*
     *
     * Advanced by the projector, which already runs per frame — see `setAnchors`. One signal
     * for both, so the HUD has a single notion of "now" and a cooldown cannot tick at a
     * different rate from the thing it is drawn on.
     */
    const [frameNow, setFrameNow] = createSignal(now());

    /**
     * When the cooldowns in `character()` were true, in client milliseconds.
     *
     * The pair — a value and the instant it was taken — is what makes counting down locally
     * honest rather than a guess: a cooldown is a deterministic countdown, so no server input
     * is needed to know it is 0.4 s shorter after 0.4 s. A fresh CHARACTER frame always wins,
     * so drift cannot accumulate past one frame's interval.
     */
    const [cooldownsAt, setCooldownsAt] = createSignal(now());

    // ── Shell ────────────────────────────────────────────────────────────
    const [phase, setPhase] = createSignal<Phase>('title');
    const [menu, setMenu] = createSignal<MenuPanel>(null);
    // Which workshop the station panel is showing. Kept beside `menu` rather than
    // inside it so the icon row can switch stations without closing and reopening.
    const [station, setStation] = createSignal<StationKind | null>(null);
    /**
     * The developer panel's block from WELCOME, or null when the active league permits
     * nothing.
     *
     * **Null is what hides the panel**, and the client never infers it: which commands
     * are unlocked is a league parameter and the spawnable catalogue is server content.
     * The server re-checks the gate on every command regardless, so this is an
     * affordance and not a permission.
     */
    const [testMode, setTestMode] = createSignal<TestModeInfo | null>(null);
    /**
     * Why the last developer command was refused, or null.
     *
     * Its own signal rather than a fifth `kind` on `notice`, because that notice lives
     * in the action bar's corner and these answers belong beside the button that asked
     * — most of them are content facts ("that base does not drop until item level 40")
     * that only make sense next to the field they are about.
     */
    const [testNotice, setTestNotice] = createSignal<RefusalReason | null>(null);
    /**
     * Why the last skill rank was refused, and which skill it was about.
     *
     * Its own signal rather than a fifth `kind` on `notice`, the same call
     * `testNotice` makes: that notice lives in the action bar's corner, and this
     * answer belongs on the row the player clicked. Kept until the next one rather
     * than aged out, because it is a fact about a decision they are still making.
     */
    const [rankNotice, setRankNotice] = createSignal<SkillRankNotice | null>(null);

    /**
     * The level just reached, for the overlay — and `null` the rest of the time.
     *
     * **A signal rather than a queue**, unlike casts and blasts. Those are world effects
     * and every one of them has to be drawn; this is a banner, and crossing three
     * thresholds on one boss kill should say *Level 8*, not run three overlays back to
     * back while the player is still being hit. The last level wins, which is the one the
     * player is actually on.
     *
     * Cleared by the overlay when its animation ends rather than on a timer here: the
     * store would then hold a duration that the stylesheet actually decides, and the two
     * would drift the first time the animation was retimed.
     */
    const [levelUp, setLevelUp] = createSignal<number | null>(null);
    /**
     * The local player's cast in progress, for the cast bar — set when a cast with a window
     * starts, cleared when it resolves. A cancelled cast (a move, an attack, a roll, death) sends
     * no event of its own; the bar reads that off the snapshot state instead. See `ui/castbar.ts`.
     */
    const [castBar, setCastBar] = createSignal<CastInProgress | null>(null);

    // ── Not sent by the server yet — see state/session.ts ────────────────
    const [rebirth] = createSignal<RebirthSummary | null>(null);
    const [apocalypse] = createSignal<ApocalypseState | null>(null);

    // The zone arrives between WELCOME and the first snapshot, and again on
    // every transition.
    const [zone, setZone] = createSignal<ZonePayload | null>(null);

    // Loot on the floor, refreshed every snapshot like entities.
    const [ground, setGround] = createSignal<GroundItem[]>([]);

    // Open town portals, refreshed every snapshot for the same reason: a portal
    // appears *during* a zone, so it cannot ride the ZONE frame the way the entry and
    // the exit do. Empty almost always, and always empty in town.
    const [portals, setPortals] = createSignal<TownPortal[]>([]);

    // The zone's fallen fixtures — doors opened, barrels broken (`design/LEVEL-FIXTURES.md`). Kept
    // across snapshots, because `down` only rides the frames where something changed (and one a
    // second): absent is "no news". Cleared with the zone.
    const [fixturesDown, setFixturesDown] = createSignal<ReadonlySet<number>>(new Set(), {
      equals: (a, b) => a.size === b.size && [...a].every((id) => b.has(id)),
    });

    // The two containers, pushed only when they change and only to their owner.
    //
    // Whole `ContainerPayload`s rather than a list plus a pair of dimensions, because
    // the grid panels need all three together and a size that lagged its contents by a
    // frame would draw an item outside its own container.
    //
    // Seeded 0 × 0 and empty, which renders as "nothing here" rather than as a grid
    // with no cells in it: before the first INVENTORY frame the client genuinely does
    // not know how big the bag is, and inventing 10 × 4 would be a guess that happened
    // to be right today.
    const [bag, setBag] = createSignal<ContainerPayload>(EMPTY_CONTAINER);
    const [vault, setVault] = createSignal<VaultPayload>(EMPTY_VAULT);
    // The gold balance rides the INVENTORY frame, because the bag and the balance
    // always change together. **Nothing spends it yet**, so the HUD shows the number
    // without implying prices exist.
    // Gold and the shop, which arrive on the same frame for the same reason: a purchase
    // moves the balance and the shelf in one instant.
    const [gold, setGold] = createSignal(0);
    // What the stash holds. Apart from the purse because that is the point: carried gold
    // is what a death spills, and banked gold survives it.
    const [stashedGold, setStashedGold] = createSignal(0);
    const [vendor, setVendor] = createSignal<VendorPayload>({ rerollInSec: 0, offers: [] });

    // The character sheet, pushed on change like the inventory. Null until the
    // first CHARACTER frame lands, which is why the sheet renders a waiting
    // state rather than a page of zeroes.
    const [character, setCharacter] = createSignal<CharacterPayload | null>(null);

    // Render settings. Seeded from the URL flags so `?fog=0` still wins at boot,
    // then overlaid with whatever the player saved. Applied to the scene by
    // main.tsx, the one place the two halves are allowed to meet.
    const [settings, setSettings] = createSignal<Settings>(
      // **Capability and preference, in that order.** `renderFeatures` says what this
      // environment *can* do — a `?fog=0`, safe mode, a driver that refused the shader —
      // and `DEFAULT_SETTINGS` says what we *want*. The conjunction means an absent
      // capability always wins; a preference can never switch on something the scene
      // cannot build.
      loadSettings(storage, {
        ...DEFAULT_SETTINGS,
        pixelate: DEFAULT_SETTINGS.pixelate && renderFeatures.pixelate,
        grading: DEFAULT_SETTINGS.grading && renderFeatures.grading,
        fog: DEFAULT_SETTINGS.fog && renderFeatures.fog,
        shadows: DEFAULT_SETTINGS.shadows && renderFeatures.shadows,
      }),
    );

    // Called when the player leaves for the title screen, so main.tsx can close
    // the socket. A sink rather than a prop threaded through the component tree,
    // matching how commands already leave the store.
    let exitSink: () => void = () => {};
    // How a drop is collected: walk to it, then pick it up. Wired by `attachInput`, which owns the
    // navigator; until then (and in tests with no input) it is a plain pickup, as it always was.
    let collectSink: (lootId: string) => void = (lootId) => commandSink(pickup(lootId));
    /** Where snapshot events go besides the damage overlay — the mixer. */
    let eventSink: ((events: readonly GameEvent[]) => void) | null = null;

    // A setting the running scene cannot honour — the pixel-art post-process
    // when `?pixelate=0` refused to build it, or when its shader failed to
    // compile. The menu greys those rather than offering a switch that does
    // nothing, which is the one thing a settings screen must never do.
    const [unavailable, setUnavailable] = createSignal<ReadonlySet<keyof Settings>>(new Set());

    // ── Loot filter (Phase 12) ───────────────────────────────────────────
    //
    // The active filter, the text it came from, and everything wrong with that
    // text. Three signals rather than one object because they change on
    // different occasions: the text changes on every keystroke a player commits,
    // the compiled filter only when the text parses, and the diagnostics only
    // when it does not.
    //
    // Seeded from storage at construction, exactly as `settings` is. A stored
    // filter that no longer parses — an older syntax, a hand-edited file — leaves
    // `filter` empty and the diagnostics on screen rather than refusing to boot:
    // the server's fail-fast rule is about content the *server* owns, and a
    // player locked out of their own game by their own typo is a worse outcome
    // than a session with no filter and a panel full of carets.
    const initialFilterText = loadFilterText(storage);
    const initialFilter = compileFilter(initialFilterText);
    const [filterText, setFilterTextSignal] = createSignal(initialFilterText);
    const [filter, setFilter] = createSignal<CompiledFilter>(initialFilter.filter);
    const [filterDiagnostics, setFilterDiagnostics] = createSignal<readonly FilterDiagnostic[]>(
      initialFilter.diagnostics,
    );
    /**
     * When the last accepted vacuum sweep happened, or null.
     *
     * Outside the reactive graph deliberately: nothing renders from it, and the only
     * reader is the throttle in `planVacuum`, which is a one-off read at keypress.
     */
    let lastVacuumAt: number | null = null;

    /**
     * Replaces the filter text, compiles it, and persists it.
     *
     * A plain function rather than only a method on the returned object, because
     * `resetFilter` calls it too and a `this.` inside an object literal breaks the
     * moment anything destructures the store — which the test helpers do.
     */
    function commitFilterText(text: string): void {
      setFilterTextSignal(text);
      saveFilterText(storage, text);
      const result = compileFilter(text, filter());
      setFilterDiagnostics(result.diagnostics);
      if (result.ok) setFilter(result.filter);
    }

    // The skill catalogue: static definitions from WELCOME, separate from the
    // per-player state in the CHARACTER frame. Two signals rather than one
    // because they change at completely different rates — the catalogue never.
    const [skillCatalogue, setSkillCatalogue] = createSignal<SkillInfo[]>([]);
    // The passive pool, likewise static and sent once in WELCOME.
    const [passiveCatalogue, setPassiveCatalogue] = createSignal<PassiveInfo[]>([]);

    /**
     * Non-reactive mirror of the latest snapshot, keyed by id. The render loop
     * reads this; nothing subscribes to it.
     */
    const raw = new Map<string, EntitySnapshot>();

    /**
     * The world point under the cursor, or null before the pointer has moved.
     *
     * Deliberately outside the reactive graph, like `raw`: it changes on every
     * pointer event, and nothing renders from it — the keyboard reads it to aim a
     * cast, which is a one-off read at keypress rather than a subscription.
     */
    let cursor: { x: number; z: number } | null = null;

    /** Ids that vanished in the last snapshot, for the scene to dispose. */
    let removed: string[] = [];

    /**
     * Casts seen in the last snapshot, waiting for the render loop.
     *
     * Drained rather than signalled, like `takeRemoved()`: an animation is a
     * render-loop concern and pushing it through the reactive graph would
     * schedule DOM work for something no component displays.
     */
    let pendingCastStarts: PendingCast[] = [];
    let pendingCasts: PendingCast[] = [];
    let pendingBlasts: PendingBlast[] = [];
    let pendingShots: PendingShot[] = [];
    let pendingDeaths: string[] = [];
    let pendingDodges: PendingDodge[] = [];

    let nextDamageId = 1;
    let commandSink: (cmd: Command) => void = () => {};

    const self = createMemo(() => entities().find((e) => e.id === selfId()));
    const dead = createMemo(() => {
      const me = self();
      return me !== undefined && me.hp <= 0;
    });
    const target = createMemo(() => {
      const id = targetId();
      return id === null ? undefined : entities().find((e) => e.id === id);
    });

    /**
     * True while a full-screen panel is up. Babylon reads this to suspend
     * click-to-move, so a click that closes a panel cannot also walk you
     * somewhere — the pass-through rule in `HUD-UX.md` §3.
     */
    const menuOpen = createMemo(() => menu() !== null);

    /**
     * How fast the local player walks, in world units per second.
     *
     * The derived value from the character sheet wins over the one in WELCOME:
     * movement-speed affixes change what the server actually integrates, and the
     * predictor has to run the *same* number or every step is a correction the
     * reconciler undoes. Falls back to the welcome value before the first
     * CHARACTER frame lands.
     */
    const playerSpeed = createMemo(() => character()?.derived.moveSpeed ?? welcomeSpeed());

    /**
     * The reach, on exactly the same rule and for exactly the same reason.
     *
     * `attack_range` is a weapon's own stat since 2026-08-07 — a bow reaches 13 and a
     * sword 2 — so the derived value wins over WELCOME's, which survives as the unarmed
     * baseline. The chase stops here, and a client still using the boot constant would
     * walk a bow user into melee before firing.
     */
    const reach = createMemo(() => character()?.derived.attackRange ?? attackRange());

    /**
     * Stamina, read off the CHARACTER frame.
     *
     * The server spends it on skill casts and recovers it after a delay, so this
     * is real data — the flat 100/100 placeholder this replaced pre-dated the
     * simulation. Before the first frame there is nothing to show, and an empty
     * bar labelled dormant is the honest answer.
     */
    /*
     * **Regeneration is integrated locally**, and it has to be.
     *
     * The server recovers both pools every tick and marks the CHARACTER frame dirty for none
     * of it — that frame is the whole stat block plus every skill and passive, and sending it
     * at 30 Hz per player would undo the bandwidth budget. So the bar held the value from
     * whenever something *else* dirtied the frame and did not move: reported as *mana regen,
     * stamina regenerating… it only updates when we attack or use a skill.*
     *
     * The split is the one the dodge cooldown already uses, and the one skill cooldowns now
     * use: a **spend** is discrete and pushed (a cast, a potion and — since 2026-08-11 — a
     * roll all mark the frame), and **recovery** is continuous and predicted from the rate the
     * frame already carries. A fresh frame always wins, so this can never be more than one
     * frame stale.
     *
     * A plain function rather than a `createMemo`: it reads a clock, so a cached value would
     * be wrong the moment it was read again. Reading `frameNow()` is what makes the bar move.
     */
    const stamina = (): StaminaState => {
      const c = character();
      if (!c) return DORMANT_STAMINA;

      // **Stamina waits before it recovers**, per 08-CLASSES-ATTRIBUTES: nothing comes back
      // for `StaminaRegenDelay` after the last expenditure. The remaining delay is on the
      // frame because neither it nor the idle timer is anything the client could work out —
      // without it a rolled-away pool appeared to start refilling instantly.
      const waited = Math.max(0, poolElapsed() - (c.pools.staminaResumesIn ?? 0));
      const current = Math.min(
        c.derived.maxStamina,
        c.pools.stamina + c.derived.staminaRegen * waited,
      );
      return { current, max: c.derived.maxStamina, live: true };
    };

    /** Seconds since the CHARACTER frame that reported the pools, never negative. */
    const poolElapsed = () => Math.max(0, (frameNow() - cooldownsAt()) / 1000);

    /**
     * Whether a roll can happen, and why not when it cannot.
     *
     * **A plain function, deliberately not a `createMemo`.** It subtracts elapsed
     * wall-clock time from the server's cooldown, so a cached value would be wrong the
     * moment it was read a second time. Reading it inside a component still tracks
     * `snapshotAt`, which changes every snapshot, so the HUD re-renders at 30 Hz — plenty
     * for a sweep — while the predictor gets the real remaining time at the instant the
     * key was pressed.
     *
     * That sub-tick precision is not a nicety. The server's value is up to 33 ms stale by
     * the time a frame renders, and refusing to predict a roll that became legal 20 ms ago
     * is the **inverse** of the bug this exists to fix: the server would grant the roll and
     * the client would snap forwards instead of back. Both directions are a 4-unit error
     * against a 3.5-unit snap threshold.
     */
    function dodgeState(): DodgeState {
      const c = character();
      const me = self();
      if (!c) return DORMANT_DODGE;

      const period = c.derived.dodgeCooldown;
      const cost = c.derived.dodgeStaminaCost;

      // Counted down from when the snapshot carrying it landed. `snapshotAt` is 0 before
      // the first one, in which case there is nothing to subtract.
      const reported = me?.dodgeCooldown ?? 0;
      const at = snapshotAt();
      const elapsed = at > 0 ? Math.max(0, (now() - at) / 1000) : 0;
      const remaining = Math.max(0, reported - elapsed);

      const staminaShort = c.pools.stamina < cost;
      return {
        ready: remaining <= 0 && !staminaShort,
        cooldownRemaining: remaining,
        cooldownFraction: period > 0 ? Math.min(1, remaining / period) : 0,
        staminaShort,
        cost,
        live: true,
      };
    }

    /**
     * Mana, read off the same frame.
     *
     * Skills spend it — `mana_cost` in the catalogue, deducted on every cast — so a
     * player watching a spell fail needs to be able to see it. It had no HUD display
     * at all, which made "not enough mana" indistinguishable from a broken button.
     */
    /**
     * Mana, integrated locally like stamina — see the note there.
     *
     * **No delay.** The spec gives stamina a pause after spending and gives mana none, so
     * mana recovers from the instant the frame reports it. That asymmetry is the design's, not
     * an omission here.
     */
    const mana = (): StaminaState => {
      const c = character();
      if (!c) return DORMANT_STAMINA;
      const current = Math.min(
        c.derived.maxMana,
        c.pools.mana + c.derived.manaRegen * poolElapsed(),
      );
      return { current, max: c.derived.maxMana, live: true };
    };

    /** True in town: no mobs, and the GRIND portal is available. */
    const inHub = createMemo(() => zone()?.kind === 'HUB');

    /**
     * True for a moment after the local player takes a hit — the screen-edge wash.
     *
     * **Derived from the damage numbers rather than stored.** A `setHurt(true)` on the
     * event plus a timer to clear it would be a second copy of a fact the store already
     * holds, and the copy is the one that can be left switched on.
     *
     * It tracks `snapshotAt` on purpose. `now()` is not reactive, so a memo over the
     * damage list alone would never recompute while the list was unchanged, and the wash
     * would hang until the next hit. `snapshotAt` moves 30 times a second, which is both
     * the resolution the flash needs and the rate the rest of the world-anchored HUD
     * already runs at.
     */
    const hurt = createMemo(() => {
      const me = selfId();
      if (me === null) return false;
      const at = snapshotAt();
      if (at === 0) return false;
      return damageNumbers().some((d) => d.entityId === me && at - d.bornAt < HURT_FLASH_MS);
    });

    /**
     * The nearest ground item to the local player, or undefined.
     *
     * Drives the pickup prompt. Computed from the reactive lists rather than the
     * raw map so the HUD updates with it.
     */
    const nearestLoot = createMemo(() => {
      const me = self();
      if (!me) return undefined;

      let best: GroundItem | undefined;
      let bestDist = Infinity;
      for (const g of ground()) {
        const d = Math.hypot(g.x - me.x, g.z - me.z);
        if (d < bestDist) {
          best = g;
          bestDist = d;
        }
      }
      return best;
    });

    // ── Loot filter: what the filter says about the floor ─────────────────
    //
    // A filter is **presentation only**. Nothing below decides what exists or what
    // may be taken: `CMD_PICKUP` is the same command a click sends and the server
    // re-checks existence, liveness, range and capacity for every one.

    /**
     * What the filter knows about where the player is standing.
     *
     * `AreaLevel` is absent in town rather than zero — the hub has no enemy level,
     * so an `AreaLevel` condition simply does not match there, which is the honest
     * reading of "this zone is level N".
     */
    const filterContext = createMemo<FilterContext>(() => {
      const z = zone();
      if (!z || z.kind !== 'COMBAT') return {};
      return { areaLevel: z.enemyLevel };
    });

    /**
     * The verdict for every drop on the floor, keyed by loot id.
     *
     * Recomputed per snapshot rather than per frame: it depends on `ground()`, which
     * the server refreshes at 30 Hz, and on `filter()`, which changes when a player
     * saves. A handful of items against a handful of rules is nothing; what would
     * cost something is deriving it from `anchors()`, which is written every frame.
     */
    const groundVerdicts = createMemo<ReadonlyMap<string, FilterVerdict>>(() => {
      const compiled = filter();
      const ctx = filterContext();
      const out = new Map<string, FilterVerdict>();
      for (const g of ground()) {
        out.set(g.id, evaluateItem(groundFilterItem(g), compiled, ctx));
      }
      return out;
    });

    function applyWelcome(payload: WelcomePayload) {
      setSelfId(payload.playerId);
      setTickRate(payload.tickRate);
      setWorldSize(payload.worldSize);
      setPlayerSpeed(payload.playerSpeed);
      setWalkRampTime(payload.walkRampTime);
      setDodgeDistance(payload.dodgeDistance);
      setDodgeDuration(payload.dodgeDuration);
      setAttackRange(payload.playerAttackRange);
      setStationRange(payload.stationRange);
      setPortalEntryRange(payload.portalEntryRange);
      setExitRange(payload.exitRange);
      setPickupRange(payload.pickupRange);
      // The skill catalogue and the passive pool are static and arrive once.
      setSkillCatalogue(payload.skills ?? []);
      setPassiveCatalogue(payload.passives ?? []);
      // Absent means the league permits no developer command, and absent must stay
      // null: it is the whole of the panel's visibility rule.
      setTestMode(payload.testMode ?? null);
    }

    function applySnapshot(payload: {
      entities: EntitySnapshot[];
      /** Ids that left. The only thing that removes an entity — see `SnapshotAssembler`. */
      gone?: readonly string[];
      ground?: GroundItem[];
      portals?: TownPortal[];
      down?: readonly number[];
      events: readonly unknown[];
    }) {
      if (payload.down) setFixturesDown(new Set(payload.down));
      const ground = payload.ground ?? [];
      // **When each drop first appeared**, for the 1.5 s impulse — the flash that is the only
      // thing to mention a Normal item, since the filter's fallback starts at Rare.
      //
      // Stamped here rather than on a `EVENT_ITEM_DROPPED`, because there is no such event: a
      // drop arrives by appearing in the ground list. Which also means "first seen" is honestly
      // first-seen-by-this-client — a drop already on the floor when a player zones in flashes
      // once, and that is the right behaviour rather than a compromise.
      if (ground.length > 0 || groundSeenAt.size > 0) {
        const present = new Set<string>();
        for (const g of ground) {
          present.add(g.id);
          if (groundSeenAt.has(g.id)) continue;
          // A drop already lying there when this client arrived is stamped as having landed long
          // ago, so it sits on the floor without announcing itself.
          groundSeenAt.set(g.id, groundPrimed ? now() : Number.NEGATIVE_INFINITY);
        }
        for (const id of groundSeenAt.keys()) {
          if (!present.has(id)) groundSeenAt.delete(id);
        }
      }
      // **Outside the guard above.** It skips the whole block when the floor is empty and nothing
      // is remembered — which is exactly the state a zone starts in, so priming inside it left the
      // flag false until the first drop *landed*, and that drop was then treated as pre-existing
      // and never flashed. An empty floor is still a floor that has been seen.
      groundPrimed = true;
      setGround(ground);
      setPortals(payload.portals ?? []);
      setSnapshotAt(now());
      // **The whole world, rebuilt from a frame that carries only the difference.**
      //
      // The wire sends an entity's description once and thereafter only the entities whose
      // visible values changed, with `gone` naming the ones that left. Everything below this
      // line — the removal rule, the interpolation buffer, the render loop — works on
      // complete snapshots and knows none of that.
      if (wire.active) {
        wire.observe({ entities: payload.entities, gone: payload.gone, events: payload.events });
      }
      const next = world.assemble(payload.entities, payload.gone);

      // Absence from the assembled world is the one rule for removing an entity — the server
      // drops dead mobs and departed players, and `SnapshotAssembler` turns that into a
      // membership the code below can diff.
      //
      // **Accumulated until the render loop drains it, never recomputed.** Snapshots arrive at
      // 30 Hz and `EntityMeshes.update` runs on the display's clock, so the moment the frame
      // rate dips below 30 — a wiped pack, a screen full of bolts, precisely when things are
      // dying — two frames land between two renders. Rebuilding this list per frame lost the
      // older frame's departures, because the ids had already been taken out of `raw` and no
      // later diff could see them again: the mesh stood where the monster died, never tipping
      // and never fading, for the rest of the session. Every sibling list here (`pendingDeaths`,
      // `pendingCasts`, …) already accumulates and clears on its own drain; this was the one
      // that did not.
      const seen = new Set(next.map((e) => e.id));
      for (const id of raw.keys()) {
        if (!seen.has(id)) removed.push(id);
      }
      for (const id of removed) raw.delete(id);
      // An id that left and came back before the renderer looked is **not** a removal. Left in
      // the list it would dispose the model that the same frame just re-registered, and the
      // rebuild would lose the interpolation history with it.
      if (removed.length > 0) removed = removed.filter((id) => !seen.has(id));
      for (const e of next) raw.set(e.id, e);

      setEntities(next);

      // A target that died or despawned is no longer a target.
      const current = targetId();
      if (current !== null && !seen.has(current)) setTargetId(null);

      // Nor is a corpse still hovered — the pointer handler will not fire again for a mesh that
      // has been disposed, so nothing else would ever clear it.
      const hovered = hoveredEntityId();
      if (hovered !== null && !seen.has(hovered)) setHoveredEntityId(null);

      // Forget when a departed entity was hit. Bounded by what is on screen rather than by the
      // session: without this a long run accumulates one entry per monster ever killed.
      if (damagedAt.size > 0) {
        for (const id of damagedAt.keys()) {
          if (!seen.has(id)) damagedAt.delete(id);
        }
      }
    }

    function applyEvents(events: readonly GameEvent[]) {
      const t = now();
      const fresh: DamageNumber[] = [];

      // Age out the last refusal, the same way damage numbers expire: on the next
      // snapshot rather than on a timer. Snapshots arrive at 30 Hz, so it clears
      // within a frame of its deadline, and there is no scheduled wake-up to do
      // what the next frame does anyway. A read-time check would not work — a memo
      // over a non-reactive clock never recomputes.
      const stale = notice();
      if (stale !== null && t - stale.bornAt >= NOTICE_TTL_MS) setNotice(null);

      // Handed on whole and before anything is derived from them, so a listener
      // sees exactly what the server sent. Audio is the only subscriber today.
      eventSink?.(events);

      for (const ev of events) {
        switch (ev.type) {
          case EVENT_ENTITY_DAMAGED: {
            const kind = damageTypeOf(ev.damageType);
            fresh.push({
              id: nextDamageId++,
              entityId: ev.entityId,
              amount: ev.amount ?? 0,
              crit: ev.crit === true,
              damageType: kind,
              absorbed: ev.absorbed ?? 0,
              bornAt: t,
            });
            // The bar's own memory. Same event, different lifetime — see `damagedAt`.
            damagedAt.set(ev.entityId, t);
            // A hit that flew gets something to have flown. The source is on the
            // event because only the server knows a monster's reach, and without the
            // bolt a fourteen-unit archer hits from off screen with no explanation.
            if (ev.ranged === true && ev.sourceId !== undefined && ev.sourceId !== '') {
              pendingShots.push({
                sourceId: ev.sourceId,
                targetId: ev.entityId,
                damageType: kind,
              });
            }
            break;
          }
          case EVENT_ENTITY_DIED:
            if (ev.entityId === targetId()) setTargetId(null);
            // **Which of the removals was a death.** The server drops a dead mob from the
            // snapshot on the same tick, and "absent from the snapshot" is the client's
            // one rule for disposing a mesh — so without this the render loop cannot tell
            // a monster that was killed from a player who disconnected, and would either
            // leave a corpse for someone who logged out or vanish the kill.
            pendingDeaths.push(ev.entityId);
            break;
          case EVENT_LEVEL_UP:
            // Own player only. Everyone's level-ups ride the zone's event stream, and a
            // banner for somebody else's would be a stranger's news across your screen.
            if (ev.entityId === selfId() && typeof ev.amount === 'number') {
              setLevelUp(ev.amount);
            }
            break;
          case EVENT_PLAYER_DIED:
            // `dead` is derived from the snapshot's HP, so there is nothing to
            // set here — the event exists for death effects and sound.
            break;
          case EVENT_SKILL_CAST_STARTED:
            // The animation's beginning. Everyone's casts, not just the local player's
            // — the event is in the snapshot the whole zone receives, so a spectator
            // sees the wind-up too.
            //
            // **And the cast bar's**, for your own cast with a window. Instant skills get none:
            // a bar that fills in no time is a flicker, not information.
            if (ev.sourceId && ev.entityId === selfId()) {
              const info = skillCatalogue().find((s) => s.skillId === ev.sourceId);
              const seconds = info?.castTimeSec ?? 0;
              setCastBar(
                seconds > 0
                  ? { skillId: ev.sourceId, name: info?.name ?? ev.sourceId, seconds, startedAt: castClock() }
                  : null,
              );
            }
            if (ev.sourceId) {
              pendingCastStarts.push({
                entityId: ev.entityId,
                skillId: ev.sourceId,
                at: castAim(ev),
                dash:
                  typeof ev.dashSec === 'number' && ev.dashSec > 0
                    ? { to: { x: ev.dashX ?? 0, z: ev.dashZ ?? 0 }, seconds: ev.dashSec }
                    : null,
              });
            }
            break;
          case EVENT_AREA_STRIKE:
            // The blast, from a player's skill or a monster's slam alike. The damage
            // already arrived per victim; this is the only thing that says *why* four
            // things took a hit at once.
            if (typeof ev.radius === 'number' && ev.radius > 0) {
              pendingBlasts.push({
                entityId: ev.entityId,
                // The skill behind it, for the colour. Absent on a monster's slam,
                // which has no skill — the same convention `EVENT_SKILL_CAST` uses.
                skillId: ev.sourceId ?? null,
                at: { x: ev.aimX ?? 0, z: ev.aimZ ?? 0 },
                radius: ev.radius,
                shape: ev.shape,
                angle: ev.angle,
                facing: ev.facing,
              });
            }
            break;
          case EVENT_SKILL_CAST:
            if (ev.entityId === selfId()) setCastBar(null);
            // The resolution. Queued separately from the start, because with a cast
            // time the two are up to 25 ticks apart and only this one may launch a
            // projectile: a bolt flies for a fixed 0.18 s, so leaving on the wind-up
            // would arrive before the thing it represents.
            if (ev.sourceId) {
              pendingCasts.push({
                entityId: ev.entityId,
                skillId: ev.sourceId,
                at: castAim(ev),
                // A resolution never carries a dash: the displacement belongs to the *start*,
                // which is the moment the client has to begin predicting it.
                dash: null,
              });
            }
            break;
          case EVENT_SKILL_REFUSED:
            // Only your own. Another player failing a cast is not your business,
            // and the server only sends yours anyway.
            if (ev.entityId === selfId() && ev.reason) {
              setNotice({ kind: 'skill', reason: ev.reason, slot: ev.amount ?? 0, bornAt: t });
            }
            break;
          case EVENT_SKILL_LEVEL_REFUSED:
            // Only your own, like every other refusal — and the server's answer
            // overwrites whatever the panel had derived from the frame, because the
            // server is the authority and a stale frame is exactly when the two differ.
            if (ev.entityId === selfId() && ev.reason && ev.sourceId) {
              setRankNotice({ skillId: ev.sourceId, reason: ev.reason });
            }
            break;
          case EVENT_SELL_REFUSED:
            // Same discipline as a refused cast or draught: the server sends a key and
            // the HUD owns the wording. A bulk button that consumed a click and said
            // nothing would be indistinguishable from a broken one.
            if (ev.entityId === selfId() && ev.reason) {
              setNotice({ kind: 'sell', reason: ev.reason, slot: 0, bornAt: t });
            }
            break;
          case EVENT_BUY_REFUSED:
            // The shelf is a button, so the player is looking at the thing that did
            // nothing. `NO_SPACE` in particular has to be said: it is the one refusal
            // where the player has the gold and would otherwise assume they had spent it.
            if (ev.entityId === selfId() && ev.reason) {
              setNotice({ kind: 'buy', reason: ev.reason, slot: 0, bornAt: t });
            }
            break;
          case EVENT_GATE_REFUSED:
            // The gate was a click, so the player is looking at the thing that did
            // nothing. Saying why is the whole of the affordance — there is no panel to
            // close and no success event to wait for, because success arrives as a whole
            // new ZONE frame.
            if (ev.entityId === selfId() && ev.reason) {
              setNotice({ kind: 'gate', reason: ev.reason, slot: 0, bornAt: t });
            }
            break;
          case EVENT_STATION_REFUSED:
            // The panel was opened locally the moment the walk arrived, so this is the
            // server disagreeing — and the only thing that can, since the stall is
            // geometry the client draws. Close it again and say why: a panel that shuts
            // itself in silence is indistinguishable from a bug.
            //
            // Guarded on the station the refusal names rather than on whichever panel is
            // up, because the answer can arrive after a tab switch and closing the wrong
            // shop is worse than the refusal it is reporting.
            if (ev.entityId === selfId() && ev.reason) {
              if (ev.station !== undefined && station() === ev.station) {
                setStation(null);
                setMenu(null);
              }
              setNotice({ kind: 'station', reason: ev.reason, slot: 0, bornAt: t });
            }
            break;
          case EVENT_DODGED:
            // Everyone's rolls, like casts: a spectator animates another player's roll
            // from this, and the direction is the one thing the snapshot omits.
            pendingDodges.push({
              entityId: ev.entityId,
              direction: { x: ev.aimX ?? 0, z: ev.aimZ ?? 0 },
            });
            break;
          case EVENT_DODGE_REFUSED:
            // Only your own, same as the other three refusals. This key is the
            // spacebar, so a silent refusal is the most confusing of the four.
            if (ev.entityId === selfId() && ev.reason) {
              setNotice({ kind: 'dodge', reason: ev.reason, slot: 0, bornAt: t });
            }
            break;
          case EVENT_ITEM_MOVE_REFUSED:
            // Only your own, like every other refusal. It carries the container as well
            // as the reason, because two grid panels can be open side by side and "no
            // space" means different things in each — and because a pickup emits this
            // too, which is the case that used to be silent entirely.
            if (ev.entityId === selfId() && ev.reason) {
              setNotice({
                kind: 'container',
                reason: ev.reason,
                slot: 0,
                container: ev.container,
                bornAt: t,
              });
            }
            break;
          case EVENT_POTION_REFUSED:
            // Same rule as a refused cast: only your own, and the server only sends
            // yours. A key that does nothing and says nothing is indistinguishable
            // from a broken one.
            if (ev.entityId === selfId() && ev.reason) {
              setNotice({ kind: 'potion', reason: ev.reason, slot: 0, bornAt: t });
            }
            break;
          case EVENT_TEST_REFUSED:
            // Only your own, like the other three refusals — and kept until the next
            // one rather than aged out, because unlike a mistimed keypress this is an
            // answer about content that a developer will want to read while retyping
            // the request.
            if (ev.entityId === selfId() && ev.reason) {
              setTestNotice(ev.reason);
            }
            break;
        }
      }

      if (fresh.length > 0) {
        setDamageNumbers((prev) => [...prev.filter((d) => t - d.bornAt < DAMAGE_TTL_MS), ...fresh]);
        // After the map is written, so anything that re-runs on this reads the new answer.
        setDamageEpoch((n) => n + 1);
      }
    }

    return {
      // ── Reactive state, for the HUD ──────────────────────────────────────
      entities,
      selfId,
      self,
      dead,
      target,
      targetId,
      /**
       * The per-frame clock, exposed so a plate can age its own damage window.
       *
       * Already driving the cooldown sweeps for the same reason: a value that changes with time
       * rather than with a frame from the server has to be read from something reactive, or it
       * sits still until an unrelated re-render moves it.
       */
      frameNow,
      /** The mob under the pointer, for the nameplate hierarchy. Written by `game/input.ts`. */
      hoveredEntityId,
      setHoveredEntityId,
      /**
       * When an entity was last damaged, or undefined. Feeds `plateVerdict`'s 3 s bar window.
       *
       * A getter rather than the `Map` itself, so nothing outside can write to it or hold a
       * reference across a zone change.
       */
      damagedAtOf: (id: string): number | undefined => damagedAt.get(id),
      /**
       * Changes whenever `damagedAtOf` might have. Read it beside that call to stay reactive —
       * see the signal's own note for why the Map alone is not enough.
       */
      damageEpoch,
      /** True while the reveal key is held. Written by `attachKeyboard`. */
      lootRevealed,
      setLootRevealed,
      /** When a drop first appeared in a snapshot, for the impulse window. */
      groundSeenAtOf: (id: string): number | undefined => groundSeenAt.get(id),
      tickRate,
      worldSize,
      playerSpeed,
      walkRampTime,
      dodgeDistance,
      dodgeDuration,
      /**
       * The current reach: the weapon's if one is equipped, the unarmed baseline
       * otherwise. `Navigator` stops a chase at it.
       */
      attackRange: reach,
      stationRange,
      portalEntryRange,
      exitRange,
      /** How close the player must stand to collect a drop. Drives the walk, not the rule. */
      pickupRange,
      /**
       * Where a drop is lying, or undefined once somebody has taken it.
       *
       * Used to route the walk to it: a click on loot across the room walks there and picks
       * it up on arrival, rather than sending a command the server refuses for range.
       */
      groundAt(id: string): { x: number; z: number } | undefined {
        const drop = ground().find((g) => g.id === id);
        return drop ? { x: drop.x, z: drop.z } : undefined;
      },
      dodgeState,

      /**
       * Says why a roll did not happen, **without waiting for the server**.
       *
       * The one refusal the client answers itself, and it can only do so because it now
       * holds both conditions: the cooldown counts down locally and the stamina pool is on
       * the CHARACTER frame. A round trip for an answer the client already has is 50–100 ms
       * of a player wondering whether the key works — and the roll itself is only 300 ms.
       *
       * The server still sends its own `EVENT_DODGE_REFUSED` for the same press, which
       * overwrites this with the identical wording. That is not redundant: the server
       * remains the authority on what happened, and if the two ever disagree the server's
       * answer is the one that survives.
       */
      refuseDodgeLocally(reason: RefusalReason): void {
        setNotice({ kind: 'dodge', reason, slot: 0, bornAt: now() });
      },
      connected,
      damageNumbers,
      /**
       * True for `HURT_FLASH_MS` after the local player is hit. Drives `HurtFlash`.
       */
      hurt,
      /** Why the last cast was refused, or null once it has aged out. */
      notice,
      anchors,

      // ── Shell ────────────────────────────────────────────────────────────
      phase,
      menu,
      menuOpen,

      // ── Awaiting a future protocol message (state/session.ts) ────────────
      rebirth,
      apocalypse,

      /**
       * The stamina bar's numbers, from the CHARACTER frame.
       *
       * Derived rather than stored: the pool and its maximum arrive in different
       * halves of the same frame — `pools.stamina` is current, `derived.maxStamina`
       * is the cap — and a second signal holding a copy would be one more thing to
       * keep in step. Dormant until the frame lands.
       */
      stamina,
      mana,

      /** The generated zone, or null before the ZONE frame arrives. */
      zone,
      inHub,

      /**
       * The town's workshops, or empty outside it.
       *
       * Straight off the ZONE frame: the server owns where a shopfront stands, and the
       * client needs the positions only to label the icons that teleport there.
       */
      stations: createMemo(() => zone()?.stations ?? []),
      /** Which workshop the station panel is showing, or null. */
      station,
      /**
       * The developer panel's block from WELCOME, or null when the league permits no
       * developer command. Null is what hides the panel and its affordance.
       */
      testMode,
      /** Why the last developer command was refused, or null. */
      testNotice,
      /**
       * The level just reached, or null once the overlay has said so.
       *
       * Own player only, and the *last* level rather than a queue of them: crossing three
       * thresholds on one kill is one piece of news, not three banners.
       */
      levelUp,
      castBar,
      /**
       * Dismisses the level banner. Called by the overlay when its animation ends, so the
       * duration lives in the stylesheet that runs it rather than in two places.
       */
      clearLevelUp(): void {
        setLevelUp(null);
      },
      /** Why the last skill rank was refused and on which skill, or null. */
      rankNotice,
      /** Clears the rank answer, so a fresh purchase starts from a blank row. */
      clearRankNotice(): void {
        setRankNotice(null);
      },
      /**
       * Clears the developer panel's answer, so a fresh request starts with a blank
       * line rather than the previous refusal still under it.
       */
      clearTestNotice(): void {
        setTestNotice(null);
      },
      ground,
      /**
       * Every town portal **this player may walk into** — a place to walk into, never a click
       * target.
       *
       * Read by `WorldMarkers` for its label and by the zone renderer for the gate itself; both
       * depend on this signal (30 Hz) rather than on `anchors()` (display refresh rate), per the
       * world-anchored HUD rule.
       *
       * ── Filtered to the player's own gates ────────────────────────────────────────────
       *
       * **Town is one shared instance**, so the snapshot carries every party's return gate: the
       * frame is encoded once and broadcast, and a per-player portal list would mean a frame per
       * player in the busiest instance in the game. `PortalSnapshot.Owners` is the compromise —
       * the server says who each gate belongs to and the client draws only its own.
       *
       * Reported: *each player should always have only his teleports displayed and can't enter a
       * town portal of another player.* The second half was already true — `resumeRun` refuses a
       * gate the asker does not own — so what was wrong was drawing doors nobody could open, in a
       * town where several parties each have one standing.
       *
       * **An absent `owners` is everyone's**, which is the portal a player opens in a zone: it is
       * theirs by construction and the server does not spend bytes saying so.
       */
      portals: createMemo(() => {
        const me = selfId();
        return portals().filter((portal) => {
          if (!portal.owners || portal.owners.length === 0) return true;
          return me !== null && portal.owners.includes(me);
        });
      }),
      nearestLoot,
      /** The ids of this zone's fixtures that no longer stand. */
      fixturesDown,

      // ── Loot filter (Phase 12) ─────────────────────────────────────────
      /** The `.grindfilter` source the player last committed. */
      filterText,
      /** The compiled filter driving labels, beams, chimes and the vacuum. */
      filter,
      /** Everything wrong with `filterText`. Empty when it parsed. */
      filterDiagnostics,
      /**
       * True once any rule is loaded.
       *
       * Read by nothing that *hides* anything — hiding is `ruleHides`'s job and only a
       * rule can do it — but the vacuum needs it: with no rules, `evaluateItem` falls
       * through to the built-in default, which never auto-picks, so `F` is honestly
       * inert rather than mysteriously silent.
       */
      filterActive: createMemo(() => filter().rules.length > 0),
      /** Every ground drop's verdict, keyed by loot id. */
      groundVerdicts,
      /** One drop's verdict, or undefined if it is not on this floor. */
      filterVerdict(lootId: string): FilterVerdict | undefined {
        return groundVerdicts().get(lootId);
      },
      /** The zone context the filter is evaluated against. */
      filterContext,
      /**
       * How many drops a vacuum press would ask for right now.
       *
       * Ignores the throttle, so the affordance answers "is there anything here" rather
       * than "may I press it this instant" — a hint that blinked off for 300 ms after
       * every press would read as the loot having gone.
       */
      vacuumCount(): number {
        const me = self();
        if (!me) return 0;
        return vacuumCandidateCount(ground(), filter(), { x: me.x, z: me.z }, {
          context: filterContext(),
        });
      },
      /**
       * The `F` vacuum: one `CMD_PICKUP` per wanted drop, nearest first.
       *
       * **Exactly the command a click sends**, through the same sink, so a modified
       * client gains nothing by calling this — the server re-checks every one. The
       * throttle and the batch cap are in `planVacuum`; this is the half that owns the
       * clock and the socket. Returns the plan so a caller (and a test) can see what was
       * asked for and why nothing was.
       *
       * The origin is the **snapshot** position rather than the predictor's, which is up
       * to one tick behind. That is deliberate: the server decides range from its own
       * position anyway, so using the client's guess could only ever ask for something
       * it was about to refuse.
       */
      vacuumLoot(at: number = now()): VacuumPlan {
        const me = self();
        if (!me) return { ids: [], refusal: 'NOTHING_TO_TAKE', wanted: 0 };

        const plan = planVacuum({
          ground: ground(),
          filter: filter(),
          origin: { x: me.x, z: me.z },
          now: at,
          lastAt: lastVacuumAt,
          context: filterContext(),
        });

        // Only an accepted sweep arms the throttle. A press that found nothing must not
        // lock the key for 300 ms — the player walked two steps and there is loot now.
        if (plan.ids.length > 0) lastVacuumAt = at;
        for (const id of plan.ids) commandSink(pickup(id));
        return plan;
      },
      /**
       * Replaces the filter text, compiles it, and persists it.
       *
       * The text is saved **whether or not it parses**: it is the player's work, and
       * throwing away a half-written rule because they closed the panel would be worse
       * than booting into a diagnostics list. The compiled filter keeps its last good
       * value on a failure, so a typo costs the new rules and nothing else.
       */
      setFilterText: commitFilterText,
      /** Puts the starter filter back, as the editor's revert. */
      resetFilter(): void {
        commitFilterText(DEFAULT_FILTER_TEXT);
      },

      /** The carried container: its dimensions and every placement in it. */
      bag,
      /**
       * The town container: a capacity and a list. Its contents arrive wherever the
       * player is — knowing what you own is not what the town gates; reaching it is.
       */
      vault,
      /**
       * What the player is carrying, as a flat list.
       *
       * Derived from `bag()` rather than stored, so there is one answer to "what is in
       * the bag". Everything that cares about *contents* rather than *placement* reads
       * this: the upgrade badge, the sell selection, the item comparison.
       */
      inventory: createMemo(() => bag().items),
      /** What a merchant has paid. The only currency there is — see vendor.go. */
      gold,
      /** What the stash is holding, which a death does not touch. */
      stashedGold,
      /** This player's shop: what is on the shelf and how long until it turns over. */
      vendor,
      character,
      skillCatalogue,
      passiveCatalogue,
      settings,
      unavailable,

      /** True when the scene can actually honour this setting. */
      settingAvailable(key: keyof Settings): boolean {
        return !unavailable().has(key);
      },

      /** A skill's static definition, or undefined. */
      skillInfo(id: string): SkillInfo | undefined {
        return skillCatalogue().find((s) => s.skillId === id);
      },

      /** Level, experience and unspent points. Null before the first frame. */
      progression: createMemo(() => character()?.progression ?? null),

      /** The five action-bar assignments, by skill id. */
      slotted: createMemo(() => character()?.slotted ?? []),

      /** Per-player skill state, keyed by id. */
      skillState(id: string): SkillState | undefined {
        return character()?.skills.find((s) => s.skillId === id);
      },

      /**
       * How long a skill has left, counted down since the frame that reported it.
       *
       * **This is what the sweep must read**, not `skillState(id).cooldown` — that is the
       * server's number *as of the last CHARACTER frame*, and the server does not send one
       * while a cooldown runs. Rendering it directly is what froze the overlay at the value it
       * had when the spell was cast.
       *
       * Floors at zero and never rises: the local clock only subtracts. A fresh frame
       * re-synchronises both the value and its timestamp, so the server stays authoritative
       * and this can be at most one frame stale.
       */
      skillCooldown(id: string): number {
        const base = character()?.skills.find((s) => s.skillId === id)?.cooldown ?? 0;
        if (base <= 0) return 0;
        // **Clamped at zero, so a clock that has not ticked cannot lengthen a cooldown.**
        // `frameNow` starts at store creation and `cooldownsAt` is stamped when the frame
        // arrives, which is strictly later — so the first read has a *negative* elapsed, and
        // without this the sweep came out at 0.5000428 instead of 0.5. Amusing in a test and
        // meaningless on screen, but a countdown that can run backwards is not a countdown.
        const elapsed = Math.max(0, (frameNow() - cooldownsAt()) / 1000);
        return Math.max(0, base - elapsed);
      },

      /**
       * Per-player passive state, keyed by id.
       *
       * Every node is in the frame, so this is defined for the whole catalogue once
       * a CHARACTER frame has landed — unlike `skillState`, which is only populated
       * for skills that are learned or learnable.
       */
      passiveState(id: string): PassiveState | undefined {
        return character()?.passives?.find((n) => n.passiveId === id);
      },

      /**
       * The potion belt, or null before the first CHARACTER frame.
       *
       * Null rather than a plausible-looking full belt, for the reason the stamina
       * bar renders empty-and-labelled before its frame arrives: a widget showing
       * charges the player may not have is a lie they cannot see through.
       */
      belt: createMemo(() => character()?.belt ?? null),

      /** What is worn in one slot, or null. Undefined before the first frame. */
      equipped(slot: DollSlot): EquippedItem | null {
        return character()?.equipment[slot] ?? null;
      },

      // ── Non-reactive state, for the render loop ──────────────────────────
      raw,

      /**
       * Where a live entity is right now, or null once it has died or left the snapshot.
       *
       * The chase's eyes. Off `raw` rather than off the interpolation buffer on purpose:
       * the question is "would the server consider me in range", and the newest snapshot
       * is the nearest thing the client has to the server's own copy — a buffer sample is
       * deliberately 66 ms in the past, which at a monster's walking pace is a fifth of a
       * unit of error in the one measurement that decides when to stop walking.
       *
       * Non-reactive, like `raw`, because the caller is the frame loop.
       */
      entityAt(id: string): { x: number; z: number } | null {
        const snap = raw.get(id);
        if (!snap || snap.hp <= 0) return null;
        return { x: snap.x, z: snap.z };
      },
      /** Where the pointer is in the world, for aiming a cast. Null until it moves. */
      cursorWorld(): { x: number; z: number } | null {
        return cursor;
      },
      setCursorWorld(at: { x: number; z: number } | null): void {
        cursor = at;
      },
      /** Ids removed by the most recent snapshot. Valid until the next one. */
      takeRemoved(): string[] {
        const out = removed;
        removed = [];
        return out;
      },
      /**
       * Casts that **began** since the last call, for the render loop to animate.
       *
       * The animation's trigger, and the only one: a cast that is interrupted never
       * resolves, so an animation started from the resolution instead would never play
       * for the fifteen skills that can be interrupted.
       */
      takeCastStarts(): PendingCast[] {
        const out = pendingCastStarts;
        pendingCastStarts = [];
        return out;
      },
      /** Rolls seen in the last snapshot. Drained beside takeCasts and takeRemoved. */
      takeDodges(): PendingDodge[] {
        const out = pendingDodges;
        pendingDodges = [];
        return out;
      },
      takeCasts(): PendingCast[] {
        const out = pendingCasts;
        pendingCasts = [];
        return out;
      },
      /** Blasts seen in the last snapshot. Drained beside the casts. */
      takeBlasts(): PendingBlast[] {
        const out = pendingBlasts;
        pendingBlasts = [];
        return out;
      },
      /** Ranged hits seen in the last snapshot, for the render loop to draw. */
      takeShots(): PendingShot[] {
        const out = pendingShots;
        pendingShots = [];
        return out;
      },
      /**
       * Entities that died in the last snapshot, so their bodies can fall rather than
       * vanish. Drained beside the shots, and for the same reason: the render loop is
       * the only subscriber and a fact nobody drains is a leak.
       */
      takeDeaths(): string[] {
        const out = pendingDeaths;
        pendingDeaths = [];
        return out;
      },

      // ── Mutations ────────────────────────────────────────────────────────
      applyFrame(frame: ServerFrame): void {
        if (frame.type === FRAME_WELCOME) {
          applyWelcome(frame.payload);
          return;
        }
        if (frame.type === FRAME_ZONE) {
          setZone(frame.payload);
          // A new zone means the old one's loot and target are gone — and its portal
          // with them, which is the server's rule too: a portal belongs to the zone it
          // was opened in. Clearing it here rather than waiting for the next snapshot
          // keeps the marker from hanging over the new floor for a tick.
          setGround([]);
          setPortals([]);
          setFixturesDown(new Set<number>());
          setTargetId(null);
          // The new floor is arrived at, not landed on: whatever is already lying there must not
          // flash. See `groundPrimed`.
          groundSeenAt.clear();
          groundPrimed = false;
          // The old zone's entity ids do not survive the change, and the server describes
          // everything again on arrival. Keeping the cache would mean a new monster that
          // happened to reuse an id inherited a dead one's name.
          world.clear();
          wire.reset();
          return;
        }
        if (frame.type === FRAME_INVENTORY) {
          setBag(frame.payload.bag);
          setVault(frame.payload.vault);
          setGold(frame.payload.gold);
          setStashedGold(frame.payload.stashedGold);
          setVendor(frame.payload.vendor);
          return;
        }
        if (frame.type === FRAME_CHARACTER) {
          setCharacter(frame.payload);
          // A read-only view of the pools as they arrived, for `scripts/browser/`. The same
          // standing as `__grindPos` and `__grindStatus`: `import.meta.env.DEV` only, absent
          // from a production bundle, and **readable rather than writable** — a hook that could
          // set a pool would verify the HUD while proving nothing about the server, which is the
          // weaker check those probes exist to avoid settling for.
          //
          // Added while chasing a Mana Shield buff that did not appear: with the server, the
          // decoder and the component all plausible, there was no way to ask which of them had
          // the value. Now there is.
          if (import.meta.env.DEV) {
            (window as unknown as { __grindPools?: unknown }).__grindPools = frame.payload.pools;
          }
          // **Stamped on arrival.** The cooldowns in this frame were true *now*, and
          // `skillCooldown` counts down from here — so a fresh frame resynchronises the value
          // and its clock together and the local countdown can never be more than one frame
          // stale.
          setCooldownsAt(now());
          return;
        }
        if (frame.type === FRAME_PARTY) {
          setParty(frame.payload);
          return;
        }
        if (frame.type === FRAME_SOCIAL) {
          setSocial(frame.payload);
          return;
        }
        if (frame.type === FRAME_CHAT) {
          // Appended and trimmed from the front: a log that only grows is a leak on a
          // server nobody logs out of, and a chat box shows a few dozen lines at most.
          setChatLog((log) => [...log, ...frame.payload.messages].slice(-CHAT_LOG_LIMIT));
          return;
        }
        if (frame.type === FRAME_SNAPSHOT) {
          applySnapshot(frame.payload);
          applyEvents(frame.payload.events);
        }
      },

      setConnected,
      /** Whether this connection was replaced by a newer one for the same character. */
      displaced,
      /** The party, its members and their vitals. Empty `members` means no party. */
      party,
      /** Who is online, and any invite waiting to be answered. */
      social,
      /** The chat log, newest last. */
      chatLog,
      setDisplaced,
      setTarget: setTargetId,
      setAnchors,
      /**
       * Advances the HUD's clock. Called by the projector once per rendered frame, beside
       * `setAnchors`, so everything time-based in the HUD moves together.
       */
      tickFrame(at: number = now()): void {
        setFrameNow(at);
      },

      /** Leaves the title screen. The caller opens the socket. */
      enterGame(): void {
        setPhase('playing');
      },

      /** Opens a panel, or closes the current one if it is already up. */
      toggleMenu(panel: Exclude<MenuPanel, null>): void {
        setMenu((current) => (current === panel ? null : panel));
      },

      /** Opens a panel outright, whatever was up before. */
      openMenu(panel: Exclude<MenuPanel, null>): void {
        setMenu(panel);
      },

      /**
       * Opens a workshop's panel, or switches the open one to it.
       *
       * Two writes rather than one because `menu` and `station` answer different
       * questions — "is a panel up" and "which shop" — and the icon row needs the
       * second to change without the first flickering closed.
       */
      openStation(kind: StationKind): void {
        setStation(kind);
        setMenu('station');
      },

      closeMenu(): void {
        setMenu(null);
      },

      /** Flips one toggle and persists the set. */
      setSetting(key: ToggleKey, value: boolean): void {
        setSettings((current) => {
          const next = { ...current, [key]: value };
          saveSettings(storage, next);
          return next;
        });
      },

      /**
       * Sets one of the volumes, 0..1, and persists it.
       *
       * Clamped here as well as on load: a slider cannot produce an out-of-range
       * value, but nothing stops another caller, and this is the number that ends
       * up on a gain node.
       */
      setVolume(key: SliderKey, value: number): void {
        const volume = Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0;
        setSettings((current) => {
          const next = { ...current, [key]: volume };
          saveSettings(storage, next);
          return next;
        });
      },

      /**
       * Marks a setting the scene cannot honour, and forces it off.
       *
       * Called once at boot by main.tsx, which is the only thing that knows
       * whether the post-process actually exists.
       */
      markSettingUnavailable(key: keyof Settings): void {
        setUnavailable((prev) => new Set(prev).add(key));
        setSettings((current) => ({ ...current, [key]: false }));
      },

      /** Registers the teardown main.tsx runs when the player leaves. */
      onExit(fn: () => void): void {
        exitSink = fn;
      },

      /**
       * Abandons the session and goes back to the title screen.
       *
       * Everything the server told us is dropped, not merely hidden: re-entering
       * opens a fresh socket and the server assigns a new player id, so keeping
       * the old entities would leave the world full of ghosts until the first
       * snapshot replaced them.
       */
      returnToTitle(): void {
        exitSink();

        setPhase('title');
        setMenu(null);
        setEntities([]);
        world.clear();

        // Hand every id to the render loop as removed rather than just emptying
        // the map. "Absent from the snapshot" is the only rule the scene uses to
        // dispose a mesh, so clearing `raw` alone would strand every model from
        // the run behind the title screen.
        removed = [...raw.keys(), ...removed];
        raw.clear();
        setSelfId(null);
        setTargetId(null);
        setDamageNumbers([]);
        setAnchors({});
        setGround([]);
        setPortals([]);
        groundSeenAt.clear();
        groundPrimed = false;
        setZone(null);
        // Back to 0 × 0 rather than to today's dimensions: re-entering assigns a new
        // player id and the next INVENTORY frame is what says how big their containers
        // are. Keeping the old size would draw a grid for a character that no longer
        // exists.
        setBag(EMPTY_CONTAINER);
        setVault(EMPTY_VAULT);
        setGold(0);
        setStashedGold(0);
        setVendor({ rerollInSec: 0, offers: [] });
        setCharacter(null);
        setSkillCatalogue([]);
        setPassiveCatalogue([]);
        setStation(null);
        // Test mode rides WELCOME, so it belongs with the catalogues: re-entering
        // re-reads it, and leaving must not leave a panel whose league is gone.
        setTestMode(null);
        setTestNotice(null);
        setConnected(false);
        // The vacuum's throttle is session state; the filter itself is not — it is a
        // player setting, like the volume, and survives a return to the title.
        lastVacuumAt = null;
      },

      /** Drops damage numbers older than DAMAGE_TTL_MS. Called once per frame. */
      expireDamage(at: number = now()): void {
        setDamageNumbers((prev) => {
          const kept = prev.filter((d) => at - d.bornAt < DAMAGE_TTL_MS);
          return kept.length === prev.length ? prev : kept;
        });
      },

      // ── Outbound ─────────────────────────────────────────────────────────
      /** Routes a command to whatever the app wired up — normally the socket. */
      send(cmd: Command): void {
        commandSink(cmd);
      },
      onCommand(sink: (cmd: Command) => void): void {
        commandSink = sink;
      },

      /**
       * Collects a drop **the way clicking it does**: walk into `pickupRange`, then pick it up.
       *
       * A loot label used to send `CMD_PICKUP` outright — and the server ignores a pickup from
       * beyond `PickupRange` without a word (`World.pickup`), so a label clicked from anywhere but
       * on top of the item did nothing at all. Reported: *it's really hard to pick up items.*
       */
      collect(lootId: string): void {
        collectSink(lootId);
      },
      onCollect(sink: (lootId: string) => void): void {
        collectSink = sink;
      },

      /**
       * Routes each snapshot's events somewhere — the audio mixer.
       *
       * One sink, like `onCommand` and `onExit`: this is a composition seam for
       * `main.tsx`, not an event bus. Pass null to detach.
       */
      onEvent(sink: ((events: readonly GameEvent[]) => void) | null): void {
        eventSink = sink;
      },
    };
  });
}

export type GameStore = ReturnType<typeof createGameStore>;

/** Convenience type for components that only read. */
export type ReadonlyEntities = Accessor<EntitySnapshot[]>;
